import { useFocusEffect, useNavigation } from "@react-navigation/native";
import React, { createRef, useCallback, useEffect, useState } from "react";
import { View, StyleSheet, Linking, ScrollView, SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  fontWeights,
  Icon,
  Text,
  theme,
  typography,
  ErrorWarningBottomSheet,
} from "ui-components";
import { Grid, Container, Row, Column } from "ui-grid";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { CheckboxStatusType } from "../../../types";
import { PaymentSourceType } from "../../enums/payment-source";
import { useConfig } from "../../hooks/useConfig";
import { HomeParamsList } from "../../navigation/home-stack/home-stack";
import { RootState } from "../../redux/reducers";
import {
  autoPaymentSchema,
  reviewCertifiedCCPaymentSchema,
} from "./utils/validation-schemas-payment";
import { getUnitNumber, objectToQueryStringParams } from "../../utils";
import { formatDateDo, getDisplayableLocalTime } from "../../utils/date";
import { toHumanDollars } from "../../utils/money";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  setPaymentInReview,
  createPayment,
  createRecoveryPayment,
} from "../../redux/actions/actionCreators/payment";
import { UserService } from "../../api/UserService";
import { translate } from "../../i18n/translate";
import { getErrorMessageId } from "../../utils/errorCodes";
import { InfoBoxType } from "../../enums/info-box";
import { nanoid } from "nanoid/non-secure";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { PaymentAutopayFlowProgressIndicator } from "../../components/new/payment-autopay-flow/payment-autopay-flow-progress-indicator";
import { useDetermineChannel } from "../../hooks/useDetermineChannel";

export interface PaymentAutoPayReviewPaymentProps extends HomeParamsList {
  selectedPaymentMethod?: any;
  propertyName?: string;
  paymentDue?: string | number;
  totalAmount?: string | number;
  onPressPolicyCheck?: any;
  onPressSavePaymentMethodCheck?: any;
  route?: any;
}
const { CREDIT_CARD, BANK_ACCOUNT } = PaymentSourceType;

const PaymentAutoPayReviewPayment = (props: PaymentAutoPayReviewPaymentProps) => {
  const navigation = useNavigation();
  const params = props?.route?.params ? props?.route?.params : props;
  const logger = useSelector((state: RootState) => state.logger);
  let {
    propName,
    propertyUnit,
    paymentDate,
    paymentDateDisplay,
    cardType,
    last4Digits,
    reservationId,
    amounts = {},
    paymentSourceId,
    paymentSourceType,
    paymentScheduleType,
    isCertified = false,
    isDelayedDcash = false,
    isRecoveryPayment = false,
    hasRemainingDCashRecoveryPayments = false,
    blockedReservationId = null,
    selectedPaymentMethod,
  } = params;

  // These are all bigs
  const { amount, platformFee, total } = amounts;
  const config = useConfig();
  const dispatch = useDispatch();
  const isCC = paymentSourceType === CREDIT_CARD;
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const userService = new UserService(cognitoUser, dispatch);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const cardFee = propertyFees?.ccDebitCard?.feeValue
    ? (propertyFees?.ccDebitCard?.feeValue * 100).toFixed(2) + `%`
    : translate("autopay.reviewPayment.noPlatformFee");
  const formattedPlatformFee = isCC ? cardFee : toHumanDollars(platformFee);
  const nickname = selectedPaymentMethod?.nickname;

  if (params.certifiedPaymentType === "DCASH_BA") {
    cardType = translate("autopay.reviewPayment.accountEndingIn");
  }
  const bankInfoText = `${cardType} ****${last4Digits}`;
  const bankInfoTextEnding = `${translate(
    "autopay.reviewPayment.accountEndingIn"
  )} ****${last4Digits}`;

  const [isCreatingPayment, setIsCreatingPayment] = useState(false);

  const [cof, setCof] = useState<CheckboxStatusType>("checked");
  const [cancellationRefund, setCancellationRefund] = useState<CheckboxStatusType>("unchecked");
  const [returnWarning, setReturnWarning] = useState<CheckboxStatusType>("unchecked");

  const [cancellationRefundCheck, setCancellationRefundCheck] = useState<boolean>(false);
  const [returnWarningCheck, setReturnWarningCheck] = useState<boolean>(false);
  const [channel] = useDetermineChannel();
  const refPopupWarning = createRef();
  let defaultValues: any = {
    cof,
    cancellationRefund,
    returnWarning,
  };
  const queryParams: any = {
    scheduleType: paymentScheduleType === "RECURRING" ? "RECURRING" : "ONE_SHOT",
    amount,
    total,
    platformFee,
    reservationId,
    paymentSourceId,
    userNowIso: getDisplayableLocalTime(new Date()).format(),
    paymentDate,
  };

  const pdfUrl = `${
    config.docGenerationService
  }/generateCancellationPolicyPDF?${objectToQueryStringParams(queryParams)}`;

  const isContinueDisabled =
    isCreatingPayment || (returnWarningCheck && cancellationRefundCheck) ? false : true;
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(autoPaymentSchema),
    defaultValues,
  });

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentInReview(reservationId));
  }, []);
  useFocusEffect(layoutEffect);

  useEffect(() => {
    register("cof");
    if (isCC) register("cancellationRefund");
    if (isCertified) register("returnWarning");
  }, [register]);

  const onSubmit = async (data: any) => {
    const { cancellationRefund: preCancellationRefund } = data;
    const [finalCof, finalCancellationRefund] = [
      translateCheckToBoolean(cof),
      translateCheckToBoolean(preCancellationRefund),
    ];
    const payload = {
      request_id: nanoid(36),
      payment_source_id: paymentSourceId,
      reservation_id: reservationId,
      schedule_date: paymentDate,
      schedule_type: paymentScheduleType,
      amount: amounts.amount,
      // TODO: may need to change when dcash is implemented
      type: paymentScheduleType === "RECURRING" ? "RECURRING" : "ONE_SHOT",
      allow_cof: finalCof,
      cancellation_refund: finalCancellationRefund,
      is_certified: isCertified,
      is_delayed_dcash: isDelayedDcash,
    };

    if (isCertified) {
      payload.is_certified = isCertified;
    }
    let result;

    try {
      await logger.log({ payload, reservationId });
      setIsCreatingPayment(true);
      if (isRecoveryPayment) {
        payload.blocked_reservation_id = blockedReservationId;
        result = await dispatch(createRecoveryPayment(payload, reservationId));
      } else {
        result = await dispatch(createPayment(payload, reservationId));
      }
      const { payment_schedule_id } = result.payload.data;
      userService.sendFootprints({
        payment_schedule_id,
        username: userInfo.username,
        interaction_type: "PAYMENT_SCHEDULE",
        channel,
      });

      let messageBody =
        paymentScheduleType === "RECURRING"
          ? translate("autopay.reviewPayment.autoPayScheduleSuccess", {
              formattedDate: formatDateDo(paymentDate),
            })
          : isCertified
          ? translate("autopay.reviewPayment.certifiedManagerApproval")
          : translate("autopay.reviewPayment.paymentPendingManagerApproval");

      let headline = isCertified
        ? translate("autopay.reviewPayment.certifiedPaymentSubmitted")
        : translate("autopay.reviewPayment.success");

      if (isRecoveryPayment) {
        messageBody = hasRemainingDCashRecoveryPayments
          ? translate("dcashRecoveryStack.paymentSuccessRemainingBalance")
          : translate("dcashRecoveryStack.paymentSuccessNoBalance");
        headline = translate("homeStack.success");
      }

      setIsCreatingPayment(false);
      logger.infoObject({
        reservationId: reservationId,
        paymentSourceId: paymentSourceId,
        paymentScheduleType: paymentScheduleType,
        message: "Create payment succeeded",
        paymentSourceType: `AUTOPAY_${paymentSourceType}`,
      });
      navigation.navigate("PaymentAutoPayPaymentSummary", {
        ...params,
        result: result?.payload?.data,
        bankInfoText,
        createPaymentResult: {
          type: "SUCCESS",
          infoBoxType: InfoBoxType.GREEN,
          headline: headline,
          messageBody,
        },
      });
    } catch (e) {
      setIsCreatingPayment(false);
      const errorData = e?.error?.response?.data;
      await logger.error(errorData ? errorData : e);
      const serverErrorCode = e?.error?.response?.data?.code;

      const error = {
        code: serverErrorCode,
      };
      const messageId = getErrorMessageId(error);
      const isVantivError = e?.error?.response?.data?.failedZeroAuth;
      const duplicatePaymentError = e?.error?.response?.data?.duplicatePaymentError;
      const headline = isVantivError
        ? translate("autopay.reviewPayment.errors.paymentUnsuccessful")
        : isCertified
        ? translate("autopay.reviewPayment.errors.certifiedPaymentUnsuccessful")
        : translate("autopay.reviewPayment.errors.paymentUnsuccessful");
      const messageBody = isVantivError
        ? messageId
          ? translate("autopay.reviewPayment." + messageId)
          : translate("autopay.reviewPayment.errors.customGeneralPayment")
        : duplicatePaymentError
        ? translate("autopay.reviewPayment.errors.duplicatePaymentBody")
        : translate("autopay.reviewPayment.errors.useDifferentPaymentMethod");
      // pull error
      await logger.error({
        exception: errorData ? errorData : e,
        reservationId: reservationId,
        paymentSourceId: paymentSourceId,
        paymentScheduleType: paymentScheduleType,
        message: `Create payment error`,
        paymentSourceType: `AUTOPAY_${paymentSourceType}`,
      });
      navigation.navigate("PaymentAutoPayPaymentSummary", {
        ...params,
        result: result?.payload?.data,
        bankInfoText,
        createPaymentResult: {
          type: "FAILURE",
          infoBoxType: InfoBoxType.RED,
          headline,
          messageBody,
        },
      });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={{ marginTop: 20, marginBottom: 20 }}>
        <PaymentAutopayFlowProgressIndicator currentPosition={1} />
      </View>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
        <>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.cardTop}>
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.rowTitle}
                      {...automationTestingLocators.paymentAutopayReviewPayment
                        .userProperty}>{`${propName}`}</Text>
                    <Text
                      style={styles.rowTitleRight}
                      {...automationTestingLocators.paymentAutopayReviewPayment
                        .userPropertyUnit}>{`Unit ${getUnitNumber(propertyUnit)}`}</Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 12 }} />
                  <RenderSeparator />
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 12 }} />
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators.paymentAutopayReviewPayment.paymentDateText}>
                      {translate("autopay.reviewPayment.paymentDate")}
                    </Text>
                    <Text
                      style={[styles.paymentDetailsRightText]}
                      {...automationTestingLocators.paymentAutopayReviewPayment.paymentDate}>
                      {paymentDateDisplay}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 16 }} />
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators.paymentAutopayReviewPayment.paymentMethodText}>
                      {translate("autopay.reviewPayment.paymentMethod")}
                    </Text>
                    <Text
                      style={[styles.paymentDetailsRightText]}
                      {...automationTestingLocators.paymentAutopayReviewPayment
                        .paymentMethodInfo}>{`${
                      nickname
                        ? nickname.length > 27
                          ? `${nickname.substring(0, 27)}...`
                          : nickname
                        : bankInfoTextEnding
                    }`}</Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 16 }} />
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators.paymentAutopayReviewPayment.amountText}>
                      {translate("autopay.reviewPayment.amount")}
                    </Text>
                    <Text
                      style={[styles.paymentDetailsRightText]}
                      {...automationTestingLocators.paymentAutopayReviewPayment.amount}>
                      {`${translate("autopay.reviewPayment.balanceDue")} + ${translate(
                        "autopay.reviewPayment.platformFee"
                      )}`}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 16 }} />
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators.paymentAutopayReviewPayment.platformFeeText}>
                      {translate("autopay.reviewPayment.platformFee")}
                    </Text>
                    <Text
                      style={[styles.paymentDetailsRightText]}
                      {...automationTestingLocators.paymentAutopayReviewPayment.platformFee}>
                      {formattedPlatformFee}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ marginTop: 12 }} />
                  <RenderSeparator />
                </Column>
              </Row>

              {errors.cancellationRefund ? (
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={{ marginTop: 40 }} />
                    <View style={{ flex: 1 }} />
                    <View style={styles.inputRowInner}>
                      <>
                        <View
                          style={{
                            flexDirection: "row",
                            flex: 1,
                            alignContent: "center",
                            alignItems: "center",
                          }}>
                          <Icon
                            color={theme.colors.alert}
                            size={theme.iconSize.small}
                            name="error"
                          />
                          <Text
                            style={styles.alertText}
                            {...automationTestingLocators.paymentAutopayReviewPayment
                              .policyErrorText}>
                            {translate("autopay.reviewPayment.errorCancellationAndRefund")}
                          </Text>
                        </View>
                      </>
                    </View>
                    <View style={{ marginTop: 16 }} />
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
              ) : null}

              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  {!errors.cancellationRefund && <View style={{ marginTop: 40 }} />}
                  <View style={styles.inputRowInner}>
                    <View style={{ paddingTop: 3 }}>
                      <Checkbox
                        disabled={isCreatingPayment}
                        style={styles.checkboxStyle}
                        onValueChange={(newValue: boolean) => {
                          setValue("cancellationRefund", newValue ? "checked" : "unchecked");
                          setCancellationRefundCheck(newValue);
                          if (newValue) {
                            trigger();
                          }
                        }}
                        value={cancellationRefundCheck}
                        {...automationTestingLocators.paymentAutopayReviewPayment
                          .cancellationRefundPolicyCheckbox}
                      />
                    </View>
                    <Text
                      style={styles.policyText}
                      {...automationTestingLocators.paymentAutopayReviewPayment
                        .cancellationRefundPolicyText}>
                      {`${translate("autopay.reviewPayment.futureCertifiedPaymentAccept1")} `}
                      <Text onPress={() => Linking.openURL(pdfUrl)} style={{ color: "#3B53D2" }}>
                        {`${translate("autopay.reviewPayment.futureCertifiedPaymentAccept2")}`}
                      </Text>
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>

              {errors.returnWarning ? (
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={{ marginTop: 16 }} />
                    <View style={{ flex: 1 }} />
                    <View style={styles.inputRowInner}>
                      <>
                        <View
                          style={{
                            flexDirection: "row",
                            flex: 1,
                            alignContent: "center",
                            alignItems: "center",
                          }}>
                          <Icon
                            color={theme.colors.alert}
                            size={theme.iconSize.small}
                            name="error"
                          />
                          <Text
                            style={styles.alertText}
                            {...automationTestingLocators.paymentAutopayReviewPayment
                              .missingAcceptanceErrorText}>
                            {translate("autopay.reviewPayment.errorMissingAcceptanceCheckbox")}
                          </Text>
                        </View>
                      </>
                    </View>
                    <View style={{ marginTop: 16 }} />
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
              ) : null}

              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  {!errors.returnWarning && <View style={{ marginTop: 16 }} />}
                  <View style={styles.inputRowInner}>
                    <View style={{ paddingTop: 3 }}>
                      <Checkbox
                        disabled={isCreatingPayment}
                        style={styles.checkboxStyle}
                        onValueChange={(newValue: boolean) => {
                          setValue("returnWarning", newValue ? "checked" : "unchecked");
                          setReturnWarningCheck(newValue);
                          if (newValue) {
                            trigger();
                          }
                        }}
                        value={returnWarningCheck}
                        {...automationTestingLocators.paymentAutopayReviewPayment
                          .returnWarningCheckbox}
                      />
                    </View>
                    <Text
                      style={styles.policyText}
                      {...automationTestingLocators.paymentAutopayReviewPayment.returnWarningText}>
                      {`${translate("autopay.reviewPayment.returnWarningMessage")}`}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
            <ErrorWarningBottomSheet
              ref={refPopupWarning}
              height={190}
              title={translate("autopay.reviewPayment.cancelPaymentWarningTitle") as string}
              body={translate("autopay.reviewPayment.cancelPaymentWarningSubTitle") as string}
              alertType={"warning"}
              leftButtonType={"secondary"}
              leftButtonText={
                translate("autopay.reviewPayment.cancelPaymentWarningCancelButtonText") as string
              }
              rightButtonText={
                translate("autopay.reviewPayment.cancelPaymentWarningStayButtonText") as string
              }
              onPressLeftButton={() => {
                navigation.navigate("Home");
                refPopupWarning?.current?.close();
              }}
              onPressRightButton={() => refPopupWarning?.current?.close()}
              {...automationTestingLocators.paymentAutopayReviewPayment.errorWarningAlert}
            />
          </Grid>
        </>
      </ScrollView>

      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 15 }} />
        <Row>
          <Column span={{ xs: false, sm: 1, md: 1, lg: 2.5 }} />
          <Column span={{ xs: 12, sm: 10, md: 10, lg: 7 }}>
            <Button
              text={translate("autopay.reviewPayment.cancel") as string}
              onPress={() => {
                refPopupWarning?.current?.open();
              }}
              type={"secondary"}
              {...automationTestingLocators.paymentAutopayReviewPayment.cancelButton}
            />
            <View style={{ paddingVertical: 5 }}></View>
            <View style={{ paddingVertical: 5 }}>
              <Button
                textStyle={{ fontFamily: "Lato_700Bold" }}
                type={"primary"}
                text={translate("autopay.reviewPayment.completeAutoPaySetup") as string}
                loading={isCreatingPayment}
                disabled={isCreatingPayment}
                style={{
                  backgroundColor:
                    isCreatingPayment || isContinueDisabled
                      ? theme.colors.text2
                      : theme.colors.primary,
                }}
                onPress={handleSubmit(onSubmit)}
                {...automationTestingLocators.paymentAutopayReviewPayment
                  .completeAutoPaySetupButton}
              />
            </View>
          </Column>
          <Column span={{ xs: false, sm: 1, md: 1, lg: 2.5 }} />
        </Row>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

const RenderSeparator = () => {
  return <View style={styles.separator} />;
};

const translateCheckToBoolean = (label: string) => {
  switch (label) {
    case "indeterminate":
      return false;
    case "checked":
      return true;
    case "unchecked":
      return false;
    default:
      return false;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  scrollView: {
    flexGrow: 0,
  },
  cardTop: {
    paddingTop: 8,
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF" },
      webTablet: {
        paddingTop: 16,
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingTop: 16,
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: "#FFFFFF" },
      iosTablet: {
        paddingTop: 16,
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: "#FFFFFF" },
      androidTablet: {
        paddingTop: 16,
        backgroundColor: "#FFFFFF",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
  separator: {
    borderColor: theme.colors.stroke,
    borderTopWidth: 0.7,
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
      webDesktop: {
        marginLeft: 26,
        marginRight: 26,
      },
      iosPhone: {},
      iosTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
      androidPhone: {},
      androidTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
    }),
  },
  paymentDetailsLeftText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Regular,
    flex: 0.4,
  },
  paymentDetailsRightText: {
    ...typography.title2,
    color: theme.colors.text4,
    fontWeight: fontWeights.Regular,
    textAlign: "right",
    marginLeft: 10,
    flex: 0.6,
  },
  rowTitle: {
    ...typography.title2,
    fontFamily: "Lato_700Bold",
    color: theme.colors.text4,
    flex: 1,
  },
  rowTitleRight: {
    ...typography.title2,
    color: theme.colors.text4,
    fontFamily: "Lato_700Bold",
  },
  policyText: {
    ...typography.title2,
    color: theme.colors.text3,
    marginLeft: 10,
    flex: 1,
  },
  alertText: {
    ...typography.body,
    color: theme.colors.alert,
    marginLeft: 10,
    flex: 1,
  },
  checkboxStyle: {
    width: 20,
    height: 20,
  },
});
export default PaymentAutoPayReviewPayment;
